import { TransactionsSchema } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { SupportedHostAppSchema } from 'renderer/features/products-page/schemas/products.schemas';
import { NewPaymentMethodFormSchema } from 'renderer/features/settings/schemas/paymentMethods.schemas';
import { z } from 'zod';
import { PricesSchema } from './prices.schema';

export const PurchaseOptionsSchema = z.object({
  // productId: z.string(),
  selectedPlan: z.string().optional(),
  // isLoggedIn: z.boolean(),
  hostApplication: SupportedHostAppSchema,
  serial: z.string().min(1, 'Serial is required'),
  platform: z.string(),
  discountCode: z.string().optional(),
  numberOfCopies: z.number(),
});

export type PurchaseOptions = z.infer<typeof PurchaseOptionsSchema>;

export const LoggedInUserExistingCardSchema = PurchaseOptionsSchema.extend({
  existingPaymentMethodToken: z
    .string()
    .min(1, 'Please choose a payment method'),
});

export const LoggedInUserNewCardSchema = PurchaseOptionsSchema.merge(
  NewPaymentMethodFormSchema,
);

export const AccountFormSchema = z.object({
  createAccount: z.boolean(),
  password: z.string(),
  confirmPassword: z.string(),
  email: z.string().email({ message: 'Invalid email format' }),
  firstName: z.string().min(1, { message: 'First name required' }),
  lastName: z.string().min(1, { message: 'Last name required' }),
  savePayment: z.boolean(),
  makeDefault: z.boolean(),
});

const NewAccountFormSchemaUnrefined = AccountFormSchema.extend({
  createAccount: z.literal(true),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must contain at least 8 characters'),
});

export type AccountForm = z.infer<typeof AccountFormSchema>;

export const PublicUserNoAccountSchema =
  LoggedInUserNewCardSchema.merge(AccountFormSchema);

const PublicUserNewAccountSchemaUnrefined = PublicUserNoAccountSchema.merge(
  NewAccountFormSchemaUnrefined,
);

export const PublicUserNewAccountSchema =
  PublicUserNewAccountSchemaUnrefined.refine(
    (data) => data.password === data.confirmPassword,
    {
      message: 'Passwords must match',
      path: ['confirmPassword'],
    },
  );

// Purchase
export const PurchaseFormSchema = PublicUserNewAccountSchemaUnrefined.extend({
  createAccount: z.boolean(),
  isNewPaymentMethod: z.boolean(),
  existingPaymentMethodToken: z.string(),
  newPaymentMethodToken: z.string(),
});

// export const PurchaseRequestSchema = PublicUserNewAccountSchemaUnrefined.extend(
//   {
//     paymentMethod: CardDetailsSchema,
//     createAccount: z.boolean(),
//     // isNewPaymentMethod: z.boolean(),
//     // existingPaymentMethodToken: z.string(),
//     // newPaymentMethodToken: z.string(),
//   },
// );

const UserSchema = z.object({
  // token: z.string().min(1, { message: 'Payment method required' }),
  // isNew: z.boolean(),

  id: z.string().optional(),
  create: z.boolean(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
});

const BillingAddressSchema = z.object({
  name: z.string().optional(),
  streetAddress: z.string().optional(),
  locality: z.string().optional(),
  region: z.string().optional(),
  postalCode: z.string().optional(),
  countryName: z.string().optional(),
});

const PaymentMethodSchema = z.object({
  id: z.string().optional(),
  create: z.boolean().optional(),
  token: z.string().optional(),
  billingAddress: BillingAddressSchema.optional(),
  provider: z.string(),
});

const PurchaseSchema = z.object({
  productId: z.string(),
  priceId: z.string().optional(),
  hostApplication: SupportedHostAppSchema,
  serial: z.string(),
  platform: z.string(),
  discountCode: z.string().optional(),
  numberOfCopies: z.number(),
});

export const PurchaseRequestSchema = z.object({
  // paymentMethod: CardDetailsSchema,
  // createAccount: z.boolean(),
  // isNewPaymentMethod: z.boolean(),
  // existingPaymentMethodToken: z.string(),
  // newPaymentMethodToken: z.string(),

  user: UserSchema,
  paymentMethod: PaymentMethodSchema,
  purchase: PurchaseSchema,
});

// const PurchaseFormSchemaOld = z.object({
//   name: z.string(),
//   email: z.string(),
//   firstName: z.string(),
//   lastName: z.string(),
//   streetAddress: z.string(),
//   locality: z.string(),
//   region: z.string(),
//   regionCode: z.string(),
//   postalCode: z.string(),
//   countryName: z.string(),
//   countryCode: z.string(),
//   password: z.string(),
//   confirmPassword: z.string(),
//   createAccount: z.boolean(),
//   savePayment: z.boolean(),
//   isNewPaymentMethod: z.boolean(),
//   // newPaymentMethodToken: z.string(),
//   existingPaymentMethodToken: z.string(),
//   discountCode: z.string(),
//   hostApplication: SupportedHostAppSchema,
//   platform: z.string(),
//   numberOfCopies: z.number(),
//   selectedPlan: z.string(),
// });

export const PurchaseResponseSchema = z.object({
  customerName: z.string(),
  productName: z.string(),
  prices: PricesSchema,
  transactionId: z.string(),
  purchaseDate: z.string(),
  to: z.string(),
  companyName: z.string(),
  serialNumbers: z.string().array(),
  transactions: TransactionsSchema,
});

export type PurchaseForm = z.infer<typeof PurchaseFormSchema>;

export type PurchaseRequest = z.infer<typeof PurchaseRequestSchema>;
// export interface PurchaseRequestOld extends PurchaseForm {
//   isLoggedIn: boolean;
//   paymentMethod: {
//     isNew: boolean;
//     token: string;
//   };
// }
export type PurchaseResponse = z.infer<typeof PurchaseResponseSchema>;
export type PurchaseConfirmation = PurchaseResponse;

// Braintree client token
export const GetConfigResponseSchema = z.object({
  stripeClientToken: z.string(),
  braintreeClientToken: z.string(),
  recaptchaSiteKey: z.string(),
});

export type GetConfigResponse = z.infer<typeof GetConfigResponseSchema>;
export type GetConfigRequest = void;

// Retrieve exising payment methods
export const ListPaymentMethodsResponseSchema = z
  .object({
    _id: z.string(),
    default: z.boolean(),
    last4: z.string(),
    expirationMonth: z.string(),
    expirationYear: z.string(),
    // customerId: z.string(),
    // isDefault: z.boolean(),
    // token: z.string(),
    cardType: z.string(), // For example: 'credit_card', 'paypal_account', etc.
    // name: z.string(),
    // address: z.string(),
    // city: z.string(),
    // state: z.string(),
    // zip: z.string(),
    // country: z.string(),
    // billingAddress: z.object({
    //   streetAddress: z.string().optional(),
    //   extendedAddress: z.string().optional(),
    //   locality: z.string().optional(),
    //   region: z.string().optional(),
    //   postalCode: z.string().optional(),
    //   countryName: z.string().optional()
    // }).optional(),
  })
  .array();

export type ListPaymentMethodsResponse = z.infer<
  typeof ListPaymentMethodsResponseSchema
>;
export type ListPaymentMethodsRequest = void;
